<!--
  DSFinV-K-Datenexport (#770).

  Two cards: the request form, then the runs. Both reflow rather than switch layouts — the form is one column
  on a phone and two from `sm` up, and every row inside a run card stacks below `sm`.
-->
<section class="mx-auto flex w-full max-w-4xl flex-col gap-4 px-4 py-6 sm:gap-6 sm:py-8">
  <header class="flex flex-col gap-1">
    <h1 class="text-2xl font-bold tracking-tight text-gray-900">DSFinV-K-Datenexport</h1>
    <p class="text-sm text-gray-500">
      Erstellen Sie für einen Zeitraum den maschinenlesbaren Datensatz Ihrer Kassendaten: für die
      Kassen-Nachschau, die Betriebsprüfung oder Ihre Steuerberatung. Fertige Exporte bleiben hier stehen und
      können jederzeit erneut heruntergeladen werden.
    </p>
  </header>

  @if (message(); as text) {
    <p class="rounded-xl px-4 py-3 text-sm"
       [class.bg-green-50]="messageIsSuccess()"
       [class.text-green-700]="messageIsSuccess()"
       [class.bg-red-50]="!messageIsSuccess()"
       [class.text-red-700]="!messageIsSuccess()"
       [attr.role]="messageIsSuccess() ? 'status' : 'alert'">{{ text }}</p>
  }

  <!-- ── Neuen Export erstellen ─────────────────────────────────────────────────────────────────────── -->
  <section class="rounded-3xl border border-gray-100 bg-white p-4 shadow-sm sm:p-6">
    <h2 class="text-base font-bold tracking-tight text-gray-900">Neuen Export erstellen</h2>

    <div class="mt-4 grid grid-cols-2 gap-2 sm:gap-3">
      <!--
        min-w-0 + an explicit height + appearance-none: a native date input in a grid overflows its card on
        iOS Safari and collapses when empty (Bug #646). The values are calendar days, never browser-local
        dates.
      -->
      <div class="min-w-0">
        <label class="mb-1 block text-xs font-medium text-gray-500" for="export-from">Von</label>
        <input type="date" id="export-from" [max]="today()"
               [ngModel]="from()" (ngModelChange)="from.set($event)"
               class="block h-10 w-full min-w-0 max-w-full appearance-none rounded-lg border border-gray-200
                      bg-white px-3 py-2 text-left text-sm text-gray-900 shadow-sm
                      focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500
                      [&::-webkit-date-and-time-value]:text-left">
      </div>
      <div class="min-w-0">
        <label class="mb-1 block text-xs font-medium text-gray-500" for="export-to">Bis</label>
        <input type="date" id="export-to" [max]="today()"
               [ngModel]="to()" (ngModelChange)="to.set($event)"
               class="block h-10 w-full min-w-0 max-w-full appearance-none rounded-lg border border-gray-200
                      bg-white px-3 py-2 text-left text-sm text-gray-900 shadow-sm
                      focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500
                      [&::-webkit-date-and-time-value]:text-left">
      </div>
    </div>

    <div class="mt-4">
      <label class="mb-2 block text-sm font-bold tracking-tight text-gray-700" for="export-registers">
        Kassen
      </label>
      <cqme-select id="export-registers" size="md" [multiple]="true" [clearable]="true"
                   [options]="selectableRegisters()" labelKey="name" valueKey="id"
                   [placeholder]="allRegistersOffered ? 'Alle Kassen' : 'Kassen wählen'"
                   ariaLabel="Kassen, die der Export umfassen soll"
                   [current]="selectedRegisterIds().length > 0 ? selectedRegisterIds() : undefined"
                   (currentChange)="onRegistersChanged($event)"/>
      <p class="mt-2 text-xs text-gray-500">
        @if (allRegistersOffered) {
          Ohne Auswahl umfasst der Export alle Kassen dieses Standorts.
        } @else {
          Dieser Demo-Standort teilt seine Kassenführung mit anderen Demo-Standorten. Wählen Sie deshalb die
          Kassen aus, die der Export umfassen soll.
        }
        Wählen Sie einzelne Kassen, erhalten Sie
        je Kasse eine eigene Datei. Wurde eine Kasse im gewählten Zeitraum neu eingerichtet, etwa nach einem
        Gerätetausch, einer erneuten Anmeldung oder einem Wechsel der technischen Sicherheitseinrichtung,
        erhalten Sie für sie mehrere Dateien („Teil 1 von 2“ …). Zusammen decken sie den Zeitraum vollständig ab.
      </p>
      <!--
        Zwei getrennte Sätze, weil sie zwei verschiedene Dinge leisten: Der erste benennt die Kassen, die der
        Leser wiedererkennen kann. Der zweite zählt die übrigen, weil eine Kasse ohne Namen sich durch keine
        Formulierung unterscheiden lässt - ihre Kennung zu drucken beantwortet niemandes Frage (#770, live screen).
      -->
      @if (unregisteredNames(); as names) {
        <p class="mt-1 text-xs text-gray-600">
          Für folgende Kassen liegen keine Daten vor, weil sie nicht für die Kassenführung eingerichtet sind:
          {{ names }}.
        </p>
      }
      @if (unnamedUnregisteredNote(); as note) {
        <p class="mt-1 text-xs text-gray-600">{{ note }}</p>
      }
    </div>

    <!--
      Archivformat (#823): eine echte Auswahl, ZIP vorausgewählt. `role="group"` mit
      aria-labelledby/-describedby bleibt, damit Beschriftung und Hinweis zusammen mit dem Steuerelement
      ankommen.
    -->
    <div class="mt-4" role="group" aria-labelledby="export-format-label"
         aria-describedby="export-format-note">
      <label id="export-format-label" class="mb-2 block text-sm font-bold tracking-tight text-gray-700"
             for="export-format">
        Archivformat
      </label>
      <cqme-select id="export-format" size="md" [options]="formatOptions" labelKey="label" valueKey="value"
                   ariaLabel="Archivformat des Exports"
                   [current]="format()" (currentChange)="onFormatChanged($event)"/>
      <p id="export-format-note" class="mt-2 text-xs text-gray-500">
        ZIP lässt sich in gängigen Prüf-Werkzeugen direkt öffnen. Frühere Exporte behalten ihr eigenes
        Archivformat, auch wenn Sie hier eine andere Auswahl treffen.
      </p>
    </div>

    <div class="mt-5 flex flex-wrap items-center gap-3">
      <button type="button"
              class="inline-flex items-center justify-center rounded-2xl bg-theme-primary-600 px-5 py-2.5
                     font-semibold text-white shadow-lg shadow-theme-primary-100 transition-all duration-200
                     hover:bg-theme-primary-700 hover:shadow-xl active:scale-95
                     disabled:cursor-not-allowed disabled:opacity-30 disabled:grayscale"
              [disabled]="!canCreate()" (click)="create()">
        {{ creating() ? 'Wird angefordert …' : 'Export erstellen' }}
      </button>
      @if (!creating() && !periodComplete()) {
        <span class="text-xs font-semibold text-red-600">
          Bitte geben Sie ein gültiges Von- und Bis-Datum an.
        </span>
      } @else if (!creating() && !periodOrderValid()) {
        <span class="text-xs font-semibold text-red-600">
          Das Bis-Datum darf nicht vor dem Von-Datum liegen.
        </span>
      }
    </div>
  </section>

  <!-- ── Bisherige Exporte ──────────────────────────────────────────────────────────────────────────── -->
  <section class="flex flex-col gap-3">
    <div class="flex flex-wrap items-center justify-between gap-2">
      <h2 class="text-base font-bold tracking-tight text-gray-900">Bisherige Exporte</h2>
      @if (live()) {
        <!-- Informational only. Every path on this screen works without the connection; it only removes a wait. -->
        <span class="inline-flex items-center gap-1.5 text-xs text-gray-500">
          <span class="h-2 w-2 rounded-full bg-green-500" aria-hidden="true"></span>
          Aktualisiert sich automatisch
        </span>
      }
    </div>

    @if (loading() && runs().length === 0) {
      <p class="text-sm text-gray-500">Exporte werden geladen …</p>
    } @else if (runs().length === 0) {
      <p class="rounded-2xl border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
        Es wurde noch kein Datenexport erstellt. Wählen Sie oben einen Zeitraum und erstellen Sie den ersten.
      </p>
    } @else {
      @for (run of runs(); track run.id) {
        <legal-data-export-run-card
          [run]="run"
          [detail]="openRunId() === run.id ? openRun() : null"
          [expanded]="openRunId() === run.id"
          [downloadingItemId]="downloadingItemId()"
          [retryingItemId]="retryingItemId()"
          (toggled)="toggle($event)"
          (download)="downloadItem($event)"
          (retry)="retryItem($event)"/>
      }
    }
  </section>
</section>
